import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { firstValueFrom } from 'rxjs';
import { ApiEndpoint } from '../../../domain/models/api-endpoint.model';

interface ProvetExplorerProxyRequestBody {
  readonly method: string;
  readonly path: string;
  readonly query?: Record<string, string>;
  readonly body?: unknown;
}

// PascalCase like every other DTO here: the API serializes with `PropertyNamingPolicy = null`
// (see `Program.cs`), so the wire shape is `{"MutationsEnabled":true}`.
export interface ProvetExplorerCapabilitiesDto {
  readonly MutationsEnabled: boolean;
}

/**
 * Sends an arbitrary request (described by a parsed `ApiEndpoint`) to the authenticated
 * `POST /api/provet-explorer/proxy` backend endpoint, which resolves the caller's own
 * ProVet connection and forwards it — no OAuth/client_secret handling here, that lives
 * entirely server-side now (see `ProVetExplorerController`).
 */
@Injectable()
export class ProvetExplorerProxyRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public request(
    endpoint: ApiEndpoint,
    pathParams: Readonly<Record<string, string>> = {},
    queryParams: Readonly<Record<string, string | number | boolean | undefined>> = {},
    body?: unknown,
  ): Promise<HttpResponse<unknown>> {
    const path = this.substitutePathParams(endpoint.path, pathParams);
    const query = this.buildQuery(queryParams);
    return this.postProxy(endpoint.method, path, query, body);
  }

  public capabilities(): Promise<ProvetExplorerCapabilitiesDto> {
    return firstValueFrom(
      this.http.get<ProvetExplorerCapabilitiesDto>(
        `${this.apiUrl}${API_ENDPOINTS.provetExplorerCapabilities}`,
      ),
    );
  }

  private postProxy(
    method: string,
    path: string,
    query: Record<string, string>,
    body?: unknown,
  ): Promise<HttpResponse<unknown>> {
    const payload: ProvetExplorerProxyRequestBody = {
      method,
      path,
      ...(Object.keys(query).length > 0 ? { query } : {}),
      ...(body !== undefined ? { body } : {}),
    };

    return firstValueFrom(
      this.http.post(`${this.apiUrl}${API_ENDPOINTS.provetExplorerProxy}`, payload, { observe: 'response' }),
    );
  }

  private substitutePathParams(path: string, pathParams: Readonly<Record<string, string>>): string {
    return path.replace(/{([^}]+)}/g, (match, paramName: string) => pathParams[paramName] ?? match);
  }

  private buildQuery(
    queryParams: Readonly<Record<string, string | number | boolean | undefined>>,
  ): Record<string, string> {
    const query: Record<string, string> = {};
    for (const [key, value] of Object.entries(queryParams)) {
      if (value !== undefined && value !== null && value !== '') {
        query[key] = String(value);
      }
    }
    return query;
  }
}
